import { createReducer, on } from '@ngrx/store';

import { BppProviderStoreModel, BppPurchaseFormStoreModel, BppSummaryStoreModel } from '@domain/backup-power-purchase';

import { BppActions } from './backup-power-purchase.actions';
import { BppGetPurchaseFormStoreModel, BppPebReportsListStoreModel, BppSubmissionsListStoreModel } from './models';

export const featureKey = 'backup-power-purchase';

export interface AppState {
  [featureKey]: FeatureState;
}

export interface FeatureState {
  isLoading: Record<string, boolean>;
  providers?: BppProviderStoreModel[];
  submissions?: BppSubmissionsListStoreModel;
  pebReports?: BppPebReportsListStoreModel;
  summary?: BppSummaryStoreModel;
  purchaseForm?: BppPurchaseFormStoreModel;
  lastPurchaseFormRequestData?: BppGetPurchaseFormStoreModel;
  crisisPurchaseForm: BppPurchaseFormStoreModel[];
  crisisLastPurchaseFormRequestData: BppGetPurchaseFormStoreModel[];
}

export const initialState: FeatureState = {
  isLoading: {},
  providers: undefined,
  submissions: undefined,
  pebReports: undefined,
  summary: undefined,
  purchaseForm: undefined,
  lastPurchaseFormRequestData: undefined,
  crisisPurchaseForm: [],
  crisisLastPurchaseFormRequestData: [],
};

export const backupPowerPurchaseReducer = createReducer(
  initialState,

  on(
    BppActions.getBppProviders,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppProviders.type]: true,
      },
    })
  ),
  on(
    BppActions.getBppProvidersSuccess,
    (state, { providers }): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppProviders.type]: false,
      },
      providers,
    })
  ),
  on(
    BppActions.getBppProvidersFail,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppProviders.type]: false,
      },
    })
  ),
  on(
    BppActions.bppProvidersCacheFound,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppProviders.type]: false,
      },
    })
  ),

  on(
    BppActions.getPebReports,
    (state, { action }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [BppActions.getPebReports.type]: true },
      pebReports: {
        ...state.pebReports,
        action,
      },
    })
  ),
  on(
    BppActions.getPebReportsSuccess,
    (state, { pebReports }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [BppActions.getPebReports.type]: false },
      pebReports: {
        ...state.pebReports,
        entries: pebReports,
      },
    })
  ),
  on(
    BppActions.getPebReportsFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [BppActions.getPebReports.type]: false },
    })
  ),

  on(
    BppActions.getBppSubmissions,
    (state, { action }): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppSubmissions.type]: true,
      },
      submissions: {
        ...state.submissions,
        action,
      },
    })
  ),
  on(
    BppActions.getBppSubmissionsSuccess,
    (state, { submissions }): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppSubmissions.type]: false,
      },
      submissions: {
        ...state.submissions,
        entries: submissions,
      },
    })
  ),
  on(
    BppActions.getBppSubmissionsFail,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppSubmissions.type]: false,
      },
    })
  ),
  on(
    BppActions.getBppSubmissionsIncorrectRequest,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppSubmissions.type]: false,
      },
      submissions: {
        ...state.submissions,
        entries: [],
      },
    })
  ),

  on(BppActions.getBppSummary, (state): FeatureState => {
    return {
      ...state,
      isLoading: { ...state.isLoading, [BppActions.getBppSummary.type]: true },
    };
  }),
  on(BppActions.getBppSummarySuccess, (state, { summary }): FeatureState => {
    return {
      ...state,
      isLoading: { ...state.isLoading, [BppActions.getBppSummary.type]: false },
      summary,
    };
  }),
  on(BppActions.getBppSummaryFail, (state): FeatureState => {
    return {
      ...state,
      isLoading: { ...state.isLoading, [BppActions.getBppSummary.type]: false },
    };
  }),

  on(
    BppActions.resubmitBppSubmission,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.resubmitBppSubmission.type]: true,
      },
    })
  ),
  on(
    BppActions.resubmitBppSubmissionSuccess,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.resubmitBppSubmission.type]: false,
      },
    })
  ),
  on(
    BppActions.resubmitBppSubmissionFail,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.resubmitBppSubmission.type]: false,
      },
    })
  ),

  on(BppActions.resubmitBppSubmissionBatch, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.resubmitBppSubmissionBatch.type]: true,
      },
    };
  }),
  on(BppActions.resubmitBppSubmissionBatchResponse, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.resubmitBppSubmissionBatch.type]: false,
      },
    };
  }),

  on(BppActions.setBppSubmissionStatus, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.setBppSubmissionStatus.type]: true,
      },
    };
  }),
  on(BppActions.setBppSubmissionStatusSuccess, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.setBppSubmissionStatus.type]: false,
      },
    };
  }),
  on(BppActions.setBppSubmissionStatusFail, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.setBppSubmissionStatus.type]: false,
      },
    };
  }),
  on(
    BppActions.setPebReportStatus,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.setPebReportStatus.type]: true,
      },
    })
  ),
  on(
    BppActions.setPebReportStatusSuccess,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.setPebReportStatus.type]: false,
      },
    })
  ),
  on(
    BppActions.setPebReportStatusFail,
    (state): FeatureState => ({
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.setPebReportStatus.type]: false,
      },
    })
  ),
  on(BppActions.getBppPurchaseFormForFrMarket, (state, { requestData }): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppPurchaseFormForFrMarket.type]: true,
      },
      lastPurchaseFormRequestData: requestData.connectedToCrisis ? state.lastPurchaseFormRequestData : requestData,
      crisisLastPurchaseFormRequestData: requestData.connectedToCrisis
        ? updateCrisisLastPurchaseFormRequestData(state.crisisLastPurchaseFormRequestData, requestData)
        : state.crisisLastPurchaseFormRequestData,
    };
  }),
  on(BppActions.getBppPurchaseFormForFrMarketSuccess, (state, { purchaseForm, connectToCrisis }): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppPurchaseFormForFrMarket.type]: false,
      },
      purchaseForm: connectToCrisis ? state.purchaseForm : purchaseForm,
      crisisPurchaseForm: connectToCrisis ? updateCrisisPurchaseForm(state.crisisPurchaseForm, purchaseForm) : state.crisisPurchaseForm,
    };
  }),
  on(BppActions.getBppPurchaseFormForFrMarketFail, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppPurchaseFormForFrMarket.type]: false,
      },
    };
  }),
  on(BppActions.getBppPurchaseFormForGbMarket, (state, { requestData }): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppPurchaseFormForGbMarket.type]: true,
      },
      lastPurchaseFormRequestData: requestData.connectedToCrisis ? state.lastPurchaseFormRequestData : requestData,
      crisisLastPurchaseFormRequestData: requestData.connectedToCrisis
        ? updateCrisisLastPurchaseFormRequestData(state.crisisLastPurchaseFormRequestData, requestData)
        : state.crisisLastPurchaseFormRequestData,
    };
  }),
  on(BppActions.getBppPurchaseFormForGbMarketSuccess, (state, { purchaseForm, connectToCrisis }): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppPurchaseFormForGbMarket.type]: false,
      },
      purchaseForm: connectToCrisis ? state.purchaseForm : purchaseForm,
      crisisPurchaseForm: connectToCrisis ? updateCrisisPurchaseForm(state.crisisPurchaseForm, purchaseForm) : state.crisisPurchaseForm,
    };
  }),
  on(BppActions.getBppPurchaseFormForGbMarketFail, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.getBppPurchaseFormForGbMarket.type]: false,
      },
    };
  }),
  on(BppActions.submitBppPurchaseFormForFrMarket, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.submitBppPurchaseFormForFrMarket.type]: true,
      },
    };
  }),
  on(BppActions.submitBppPurchaseFormForFrMarketSuccess, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.submitBppPurchaseFormForFrMarket.type]: false,
      },
    };
  }),
  on(BppActions.submitBppPurchaseFormForFrMarketFail, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.submitBppPurchaseFormForFrMarket.type]: false,
      },
    };
  }),
  on(BppActions.submitBppPurchaseFormForGbMarket, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.submitBppPurchaseFormForGbMarket.type]: true,
      },
    };
  }),
  on(BppActions.submitBppPurchaseFormForGbMarketSuccess, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.submitBppPurchaseFormForGbMarket.type]: false,
      },
    };
  }),
  on(BppActions.submitBppPurchaseFormForGbMarketFail, (state): FeatureState => {
    return {
      ...state,
      isLoading: {
        ...state.isLoading,
        [BppActions.submitBppPurchaseFormForGbMarket.type]: false,
      },
    };
  }),
  on(BppActions.clearBppPurchaseForm, (state): FeatureState => {
    return {
      ...state,
      purchaseForm: undefined,
      lastPurchaseFormRequestData: undefined,
      crisisPurchaseForm: [],
      crisisLastPurchaseFormRequestData: [],
    };
  }),
  on(BppActions.clearPebReports, (state): FeatureState => ({ ...state, pebReports: undefined })),
  on(BppActions.clearBppSubmissions, (state): FeatureState => ({ ...state, submissions: undefined })),
  on(BppActions.clearBppProviders, (state): FeatureState => ({ ...state, providers: undefined })),
  on(BppActions.clearBppSummary, (state): FeatureState => {
    return { ...state, summary: undefined };
  })
);

const updateCrisisPurchaseForm = (
  crisisPurchaseForm: BppPurchaseFormStoreModel[],
  purchaseForm: BppPurchaseFormStoreModel
): BppPurchaseFormStoreModel[] => {
  const marketPurchaseFormIndex = crisisPurchaseForm.findIndex(({ market }) => market === purchaseForm.market);

  if (marketPurchaseFormIndex === -1) {
    return [...crisisPurchaseForm, purchaseForm];
  } else {
    const newCrisisPurchaseForm = [...crisisPurchaseForm];
    newCrisisPurchaseForm.splice(marketPurchaseFormIndex, 1, purchaseForm);
    return newCrisisPurchaseForm;
  }
};

const updateCrisisLastPurchaseFormRequestData = (
  crisisLastPurchaseFormRequestData: BppGetPurchaseFormStoreModel[],
  requestData: BppGetPurchaseFormStoreModel
): BppGetPurchaseFormStoreModel[] => {
  // Always append to end so the last element stays the most recently dispatched market.
  // Filter out any prior entry for this market first (upsert semantics).
  return [...crisisLastPurchaseFormRequestData.filter(({ market }) => market !== requestData.market), requestData];
};
